import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { type NotificationDTO, type NotificationListRequestDTO, type PagedResponseDTO } from '@api/models';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { globalLoaderHeaders } from '@infra/http-interceptors/global-loader-headers';
import { CompanyDateFormatService } from '@common/services/company-date-format.service';
import { FilterService } from '@common/services/filter.service';
import { AuthService } from '@infra/auth/auth.service';
import { Globals } from '@system/globals';
import { format } from 'date-fns';
import { map, type Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly filterService = inject(FilterService);
  private readonly translateService = inject(TranslateService);
  private readonly companyDateFormatService = inject(CompanyDateFormatService);

  list(request: NotificationListRequestDTO, skipGlobalLoader = false): Observable<PagedResponseDTO<NotificationDTO>> {
    const headers = globalLoaderHeaders(skipGlobalLoader);
    const queryString = this.filterService.createQueryStringFromObject(request);
    const uri = `${environment.api}/notifications${queryString}`;
    return this.http.get<PagedResponseDTO<NotificationDTO>>(uri, { headers }).pipe(
      map(response => {
        response.data.forEach(notification => this.prepareForDisplay(notification));
        return response;
      })
    );
  }

  countUnread(): Observable<number> {
    const uri = `${environment.api}/notifications/unread-count`;
    return this.http.get<number>(uri);
  }

  readAll(): Observable<void> {
    const uri = `${environment.api}/notifications/read-all`;
    return this.http.post<void>(uri, null);
  }

  read(notificationId: string): Observable<void> {
    const uri = `${environment.api}/notifications/${notificationId}/read`;
    return this.http.put<void>(uri, null);
  }

  unread(notificationId: string): Observable<void> {
    const uri = `${environment.api}/notifications/${notificationId}/read`;
    return this.http.delete<void>(uri);
  }

  openNotification(notification: NotificationDTO, openInNewTab: boolean): Observable<void> {
    const markAsRead$ = notification.readDateTime === null ? this.read(notification.id) : of(undefined);
    return markAsRead$.pipe(tap(() => this.openUrl(notification, openInNewTab)));
  }

  private prepareForDisplay(notification: NotificationDTO): void {
    if (notification.message) {
      notification.message = this.translateService.instant(notification.message);
    }

    if (notification.addedDateTime != null) {
      // The API returns UTC with a 'Z' suffix, so the Date is already in local time.
      notification.timeToShow = format(
        new Date(notification.addedDateTime),
        `HH:mm, ${this.companyDateFormatService.dateFormat}`
      );
    }
  }

  private getUrl(notification: NotificationDTO): string {
    if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_ASSIGNMENT) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_MESSAGE) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/activity/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_TODO) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/todos/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_QUOTE) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/finance/quote/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_INVOICE) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/finance/invoice/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_LEAD) {
      return `/${this.authService.companyUrl}/leads/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_INTEGRATION_TASK) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/survey`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WAREHOUSE_SERVICE_SCHEDULE_CREATED) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/services`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WAREHOUSE_SERVICE_CHANGED) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/services`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WAREHOUSE_SERVICE_SCHEDULE_OVERDUE) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/services`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WORK_TICKET) {
      return `/${this.authService.companyUrl}/work-tickets/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_VENDOR_INVOICE) {
      return `/${this.authService.companyUrl}/vendor-invoices`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_VEHICLE) {
      return `/admin/${this.authService.companyUrlName}/vehicles/${notification.entityId}`;
    } else if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_DOCUMENT) {
      return `/${this.authService.companyUrl}/assignment/${notification.assignmentId}/documents`;
    } else {
      return '';
    }
  }

  private openUrl(notification: NotificationDTO, openInNewTab: boolean): void {
    const url = this.getUrl(notification);
    if (url) {
      if (openInNewTab) {
        window.open(url, '_blank');
      } else {
        if (notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_LEAD) {
          this.router.navigate([url]);
        } else if (
          notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WAREHOUSE_SERVICE_SCHEDULE_CHANGED ||
          notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WAREHOUSE_SERVICE_SCHEDULE_CREATED ||
          notification.entityTypeId === Globals.NOTIFICATION_ENTITY_TYPE_WAREHOUSE_SERVICE_SCHEDULE_OVERDUE
        ) {
          if (this.router.url.endsWith('/services')) {
            window.location.href = `${url}?planningPopupRevisionProductId=${notification.entityId}`;
          } else {
            this.router.navigate([url], {
              queryParams: { planningPopupRevisionProductId: notification.entityId },
              replaceUrl: true,
            });
          }
        } else if (notification.assignmentId) {
          this.router.navigate([`/${this.authService.companyUrl}/assignment/${notification.assignmentId}`]).then(() => {
            this.router.navigate([url]);
          });
        } else {
          this.router.navigate([url]);
        }
      }
    }
  }
}
